import { useEffect, useState, useCallback, useRef } from 'react';
import websocketService from '@/services/websocketService';
import { getFreshAccessToken } from '@/utils/authFetch';
import type {
  WebSocketChannel,
  BalanceUpdateEvent,
  PositionUpdateEvent,
  SignalUpdateEvent,
  PriceUpdateEvent,
} from '@/services/websocketService';
import { useAuth } from '@/contexts/AuthContext';

type EventCallbacks = {
  onBalanceUpdate?: (data: BalanceUpdateEvent) => void;
  onPositionUpdate?: (data: PositionUpdateEvent) => void;
  onSignalUpdate?: (data: SignalUpdateEvent) => void;
  onPriceUpdate?: (data: PriceUpdateEvent) => void;
};

export interface UseWebSocketOptions extends EventCallbacks {
  channels: WebSocketChannel[];
  autoConnect?: boolean;
}

export interface UseWebSocketReturn {
  isConnected: boolean;
  isConnecting: boolean;
  error: Error | null;
  connect: () => Promise<void>;
  disconnect: () => void;
  requestUpdate: () => void;
  subscribe: (channels: WebSocketChannel[]) => void;
  unsubscribe: (channels: WebSocketChannel[]) => void;
}

function useLatestRef<T>(value: T) {
  const ref = useRef(value);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref;
}

export function useWebSocket({ channels, autoConnect = true, ...callbacks }: UseWebSocketOptions): UseWebSocketReturn {
  const { isAuthenticated } = useAuth();
  const [isConnected, setIsConnected] = useState(() => websocketService.connected);
  const [isConnecting, setIsConnecting] = useState(() => websocketService.connecting);
  const [error, setError] = useState<Error | null>(null);

  const callbacksRef = useLatestRef(callbacks);
  const consumerAcquiredRef = useRef(false);

  const connect = useCallback(async () => {
    const token = await getFreshAccessToken();
    if (!token) {
      throw new Error('Cannot connect to WebSocket without authentication token');
    }

    try {
      if (!consumerAcquiredRef.current) {
        const consumerCount = websocketService.acquireConsumer();
        consumerAcquiredRef.current = true;
        if (consumerCount === 1 && !websocketService.connected) {
          setIsConnecting(true);
          await websocketService.connect(token);
        }
      } else if (!websocketService.connected) {
        setIsConnecting(true);
        await websocketService.connect(token);
      }

      setIsConnected(true);
      setIsConnecting(false);
      setError(null);
    } catch (err) {
      if (consumerAcquiredRef.current) {
        const remaining = websocketService.releaseConsumer();
        consumerAcquiredRef.current = remaining > 0;
      }
      setIsConnecting(false);
      setIsConnected(false);
      setError(err instanceof Error ? err : new Error('Failed to connect to WebSocket'));
      throw err;
    }
  }, []);

  const disconnect = useCallback(() => {
    if (!consumerAcquiredRef.current) {
      return;
    }

    const remaining = websocketService.releaseConsumer();
    consumerAcquiredRef.current = remaining > 0;

    if (remaining === 0) {
      websocketService.disconnect();
      setIsConnected(false);
      setIsConnecting(false);
    } else {
      setIsConnected(websocketService.connected);
      setIsConnecting(websocketService.connecting);
    }
  }, []);

  const subscribe = useCallback((channelsToSubscribe: WebSocketChannel[]) => {
    websocketService.subscribe(channelsToSubscribe);
  }, []);

  const unsubscribe = useCallback((channelsToUnsubscribe: WebSocketChannel[]) => {
    websocketService.unsubscribe(channelsToUnsubscribe);
  }, []);

  const requestUpdate = useCallback(() => {
    websocketService.requestUpdate();
  }, []);

  useEffect(() => {
    const offConnect = websocketService.onConnect(() => {
      setIsConnected(true);
      setIsConnecting(false);
      setError(null);
    });

    const offDisconnect = websocketService.onDisconnect(() => {
      setIsConnected(false);
      setIsConnecting(false);
    });

    const offConnectError = websocketService.onConnectError((err) => {
      setIsConnected(false);
      setIsConnecting(false);
      setError(err);
    });

    return () => {
      offConnect();
      offDisconnect();
      offConnectError();
    };
  }, []);

  const strictModeGuardRef = useRef({
    skipEffect: import.meta.env.DEV,
    skipCleanupRuns: import.meta.env.DEV ? 1 : 0,
  });

  useEffect(() => {
    if (!autoConnect || !isAuthenticated) {
      return;
    }

    const guard = strictModeGuardRef.current;

    // In development, React 18 Strict Mode mounts twice. Skip the first run to avoid
    // connecting and immediately disconnecting, which causes browser warnings.
    if (guard.skipEffect) {
      guard.skipEffect = false;
      guard.skipCleanupRuns = import.meta.env.DEV ? 1 : 0;
      return;
    }

    // Prepare to skip the first cleanup invocation that Strict Mode triggers immediately.
    guard.skipCleanupRuns = import.meta.env.DEV ? 1 : 0;

    // Track if component is still mounted to avoid logging during unmount
    let isMounted = true;

    connect().catch((err) => {
      // Silently handle connection errors in production to avoid console warnings
      if (isMounted && import.meta.env.DEV) {
        console.error('[useWebSocket] Initial connection failed:', err.message || 'Unknown error');
      }
    });

    return () => {
      if (guard.skipCleanupRuns > 0) {
        guard.skipCleanupRuns -= 1;
        return;
      }

      isMounted = false;
      disconnect();
    };
  }, [autoConnect, isAuthenticated, connect, disconnect]);

  useEffect(() => {
    if (channels.length) {
      subscribe(channels);
      return () => unsubscribe(channels);
    }
  }, [channels, subscribe, unsubscribe]);

  useEffect(() => {
    const { current } = callbacksRef;
    const cleanups: Array<() => void> = [];

    if (current.onBalanceUpdate) {
      cleanups.push(websocketService.onBalanceUpdate(current.onBalanceUpdate));
    }
    if (current.onPositionUpdate) {
      cleanups.push(websocketService.onPositionUpdate(current.onPositionUpdate));
    }
    if (current.onSignalUpdate) {
      cleanups.push(websocketService.onSignalUpdate(current.onSignalUpdate));
    }
    if (current.onPriceUpdate) {
      cleanups.push(websocketService.onPriceUpdate(current.onPriceUpdate));
    }

    return () => {
      cleanups.forEach((cleanup) => cleanup());
    };
  }, [callbacksRef]);

  return {
    isConnected,
    isConnecting,
    error,
    connect,
    disconnect,
    requestUpdate,
    subscribe,
    unsubscribe,
  };
}

export default useWebSocket;
